import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip, LinearProgress, Alert,
} from '@mui/material';
import axiosInstance from '../../utils/axios';

/**
 * Read-only roster of division members with lifetime attendance counts.
 */
export default function DivisionAttendanceRoster({ divisionId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!divisionId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axiosInstance.get(`/divisions/${divisionId}/members`);
        const list = Array.isArray(data) ? data : (data?.members || data?.items || []);
        if (!cancelled) {
          setMembers(
            [...list].sort(
              (a, b) => (b.lifetimeEventsAttended || b.attendanceCount || 0) - (a.lifetimeEventsAttended || a.attendanceCount || 0)
            )
          );
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load attendance roster');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [divisionId]);

  if (!divisionId) return null;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={700}>Division attendance roster</Typography>
        <Typography variant="caption" color="text.secondary">
          Lifetime event attendances by member (read-only).
        </Typography>
      </Box>
      {loading && <LinearProgress />}
      {error && <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Member</TableCell>
            <TableCell align="right">Events attended</TableCell>
            <TableCell align="right">Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map((m) => {
            const id = m._id || m.riderId;
            const name = m.name || m.username || m.tmpIngameName || 'Member';
            const count = m.lifetimeEventsAttended ?? m.attendanceCount ?? 0;
            return (
              <TableRow key={id}>
                <TableCell>{name}</TableCell>
                <TableCell align="right">{count}</TableCell>
                <TableCell align="right">
                  <Chip size="small" label={m.inactive ? 'Inactive' : 'Active'} color={m.inactive ? 'default' : 'success'} />
                </TableCell>
              </TableRow>
            );
          })}
          {!loading && !members.length && (
            <TableRow>
              <TableCell colSpan={3} align="center">No members found</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
